import { collection, query, getDocs, orderBy } from "firebase/firestore";
import { useState, useEffect } from "react";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import EntryDetailPage from "./EntryDetailPage";

interface LibraryEntry {
  id: string;
  title: string;
  pdfUrl?: string;
  driveLink?: string;
  blogLink?: string;
  metadata?: string;
  dataType?: string;
  author?: string;
  createdAt?: any; // Firestore Timestamp
}

const LibraryListPage = () => {
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const col = collection(fireDB, "libraryEntries");
        const q = query(col, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const arr: LibraryEntry[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          arr.push({
            id: docSnap.id,
            title: data.title,
            pdfUrl: data.pdfUrl,
            driveLink: data.driveLink,
            blogLink: data.blogLink,
            metadata: data.metadata,
            dataType: data.dataType,
            author: data.author,
            createdAt: data.createdAt,
          });
        });
        setEntries(arr);
      } catch (err) {
        console.error("Failed to load entries", err);
      }
    };

    fetchEntries();
  }, []);

  const formatDate = (ts: any) => {
    if (ts && ts.toDate) {
      return ts.toDate().toLocaleString();
    }
    return "Not found";
  };

  if (selectedEntryId) {
    return (
      <EntryDetailPage
        entryId={selectedEntryId}
        onBack={() => setSelectedEntryId(null)}
      />
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl mb-6 font-bold">📚 E-Library Entries</h1>
      <div className="space-y-4">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="bg-white p-6 rounded shadow-md border border-gray-200"
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold">{entry.title}</h2>
                <p className="text-gray-600">Type: {entry.dataType || "Not found"}</p>
                <p className="text-gray-600">Author: {entry.author || "Not found"}</p>
                <p className="text-gray-600">Date: {formatDate(entry.createdAt)}</p>

                {/* Blog Link */}
                {entry.blogLink && (
                  <p className="mt-2">
                    🔗 Blog:{" "}
                    <a
                      href={entry.blogLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Read Blog
                    </a>
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                {/* PDF Download Button */}
                {entry.pdfUrl && (
                  <a
                    href={entry.pdfUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-center"
                  >
                    📄 Download PDF
                  </a>
                )}

                {/* Drive Download Button */}
                {entry.driveLink && (
                  <a
                    href={entry.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 text-center"
                  >
                    🔗 Open Drive Link
                  </a>
                )}

                <button
                  onClick={() => setSelectedEntryId(entry.id)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  🔍 Explore
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LibraryListPage;
